
/**
 * Advanced image optimization utility
 * Handles responsive images, lazy loading, and format conversion
 */
import { getNetworkStatus, isLowDataMode } from './networkMonitor';

// Quality levels for different connection types
const QUALITY_LEVELS = {
  slow2g: 30,
  slow: 50,
  medium: 65,
  fast: 80,
  default: 75
};

// Width limits for different connection types (to save bandwidth)
const WIDTH_LIMITS = {
  slow2g: 320,
  slow: 480,
  medium: 640,
  fast: 1200,
  default: 800
};

// Detect if we're on a mobile device
const isMobile = typeof navigator !== 'undefined' ? 
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) : 
  false;

// Prioritize critical paths for LCP
const CRITICAL_PATH_PATTERNS = [
  '/images/hero', 
  'hero-', 
  'featured-', 
  'banner-',
  'logo',
  'fish-header',
  'fish-footer',
  'logos/',
  '98a9fcc4-c199-4be7-8bad-6756a3aa8870', // Legacy header logo ID
  'e78977e9-5853-478f-89b7-621e752805c7'  // Legacy footer logo ID
];

// Check if an image path is critical for LCP
export function isCriticalPath(path: string): boolean {
  return CRITICAL_PATH_PATTERNS.some(pattern => 
    path.toLowerCase().includes(pattern.toLowerCase()));
}

/**
 * Get estimated connection speed - Updated to be more conservative
 * Now treats connections with high RTT or lower downlink as medium instead of fast
 */
export function getConnectionSpeed(): 'slow2g' | 'slow' | 'medium' | 'fast' | 'default' {
  const networkStatus = getNetworkStatus();
  
  switch (networkStatus.effectiveType) {
    case 'slow-2g':
      return 'slow2g';
    case '2g':
      return 'slow';
    case '3g':
      return 'medium';
    case '4g':
      // More conservative: treat "4g" connections with higher RTT or lower downlink as medium
      if (networkStatus.rtt && networkStatus.rtt > 200) return 'medium';
      if (networkStatus.downlink && networkStatus.downlink < 2) return 'medium';
      return 'fast';
    default:
      return 'default';
  }
}

/**
 * Generate srcset for responsive images with network-aware sizes
 */
export function generateSrcSet(
  src: string,
  widths?: number[]
): string {
  if (!src) return '';
  
  const connectionSpeed = getConnectionSpeed();
  const isSlow = connectionSpeed === 'slow2g' || connectionSpeed === 'slow';
  
  // Adjust widths based on connection speed
  const defaultWidths = isMobile 
    ? (isSlow ? [160, 320, 480] : [320, 480, 640, 750])
    : [640, 768, 1024, 1280, 1536];
  
  const finalWidths = widths || defaultWidths;
  
  try {
    // Skip if already contains srcset
    if (src.includes(' ')) return src;
    
    // For Unsplash images
    if (src.includes('unsplash.com')) {
      return finalWidths
        .map(width => {
          const url = new URL(src);
          url.searchParams.set('w', width.toString());
          url.searchParams.set('auto', 'format');
          const quality = isMobile || isSlow ? 
            Math.max(30, QUALITY_LEVELS[connectionSpeed] - 10) : 
            QUALITY_LEVELS[connectionSpeed];
          url.searchParams.set('q', quality.toString());
          return `${url.toString()} ${width}w`;
        })
        .join(', ');
    }
    
    // For Supabase storage
    if (src.includes('storage.googleapis.com') || src.includes('supabase.co/storage')) {
      return finalWidths
        .map(width => {
          const separator = src.includes('?') ? '&' : '?';
          const quality = isMobile || isSlow ? 
            Math.max(30, QUALITY_LEVELS[connectionSpeed] - 10) : 
            QUALITY_LEVELS[connectionSpeed];
          return `${src}${separator}width=${width}&quality=${quality}&format=webp ${width}w`;
        })
        .join(', ');
    }
    
    // Generic implementation
    return finalWidths
      .map(width => {
        const quality = isMobile || isSlow ? 
          Math.max(30, QUALITY_LEVELS[connectionSpeed] - 10) : 
          QUALITY_LEVELS[connectionSpeed];
        return `${src}?width=${width}&quality=${quality}&format=webp ${width}w`;
      })
      .join(', ');
  } catch (e) {
    console.error('Error generating srcset:', e);
    return src;
  }
}

/**
 * Get optimal image format based on browser support
 */
export function getOptimalFormat(): 'webp' | 'avif' | 'jpeg' {
  if (typeof window === 'undefined') return 'webp';
  
  // Check for AVIF support
  const canUseAvif = document.createElement('canvas')
    .toDataURL('image/avif').includes('data:image/avif');
  
  if (canUseAvif) return 'avif';
  
  // Check for WebP support
  const canUseWebP = document.createElement('canvas')
    .toDataURL('image/webp').includes('data:image/webp');
  
  if (canUseWebP) return 'webp';
  
  // Fallback to JPEG
  return 'jpeg';
}

/**
 * Build optimized image URL based on device and network conditions
 */
export function getOptimizedImageUrl(
  url: string | null,
  options: {
    width?: number;
    quality?: number;
    format?: 'auto' | 'webp' | 'avif' | 'jpeg';
    blur?: number;
  } = {}
): string {
  if (!url) return 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d';
  
  const connectionSpeed = getConnectionSpeed();
  const format = options.format === 'auto' ? getOptimalFormat() : (options.format || 'webp');
  
  // Check if this is a critical image path
  const isCritical = isCriticalPath(url);
  
  // If it's a development environment, don't optimize to avoid issues
  if (process.env.NODE_ENV === 'development' && !isCritical) {
    return url;
  }
  
  // Adjust quality based on connection speed, device type, and image importance
  let quality = options.quality;
  if (!quality) {
    // Mobile devices get lower quality images by default to save bandwidth
    const deviceAdjustment = isMobile ? -10 : 0;
    // Critical images get higher quality even on slow connections
    const criticalBoost = isCritical ? 15 : 0;
    
    quality = QUALITY_LEVELS[connectionSpeed] + deviceAdjustment + criticalBoost;
    quality = Math.max(20, Math.min(quality, 85)); // Ensure quality stays between 20-85
  }
  
  // Adjust width based on device and connection
  let width = options.width;
  if (!width) {
    // Set maximum width based on connection speed
    const maxWidth = WIDTH_LIMITS[connectionSpeed];
    
    if (isMobile) {
      width = Math.min(window.innerWidth, maxWidth);
    } else {
      width = maxWidth;
    }
    
    // Ensure critical images are appropriately sized
    if (isCritical) {
      width = Math.max(width, isMobile ? 320 : 640);
    }
  }
  
  try {
    // For Unsplash images
    if (url.includes('unsplash.com')) {
      const imageUrl = new URL(url);
      imageUrl.searchParams.set('w', width.toString());
      imageUrl.searchParams.set('q', quality.toString());
      imageUrl.searchParams.set('fm', format);
      imageUrl.searchParams.set('auto', 'compress');
      
      if (options.blur && (connectionSpeed !== 'fast' || isMobile)) {
        imageUrl.searchParams.set('blur', options.blur.toString());
      }
      
      return imageUrl.toString();
    }
    
    // For Supabase storage or Google Cloud Storage
    if (url.includes('storage.googleapis.com') || url.includes('supabase.co/storage')) {
      const separator = url.includes('?') ? '&' : '?';
      let optimizedUrl = `${url}${separator}width=${width}&quality=${quality}&format=${format}`;
      
      if (options.blur && (connectionSpeed !== 'fast' || isMobile)) {
        optimizedUrl += `&blur=${options.blur}`;
      }
      
      return optimizedUrl;
    }
    
    // For our Lovable uploads
    if (url.includes('lovable-uploads')) {
      // For local assets, we can't transform them, just return as-is
      return url;
    }
    
    // Generic approach
    const separator = url.includes('?') ? '&' : '?';
    let optimizedUrl = `${url}${separator}width=${width}&quality=${quality}&format=${format}`;
    
    if (options.blur && (connectionSpeed !== 'fast' || isMobile)) {
      optimizedUrl += `&blur=${options.blur}`;
    }
    
    return optimizedUrl;
  } catch (e) {
    console.error('Error optimizing image URL:', e);
    return url;
  }
}

/**
 * Generate low quality placeholder image with network-aware settings
 * @param src Source image URL
 * @param quality Optional quality override (1-100)
 */
export function generatePlaceholderUrl(src: string, quality?: number): string {
  const connectionSpeed = getConnectionSpeed();
  // Increase size for placeholders to avoid extreme pixelation
  const size = connectionSpeed === 'slow2g' || connectionSpeed === 'slow' ? 20 : 
               (isMobile ? 25 : 30);
  
  // Use specified quality or calculate based on connection
  const autoQuality = connectionSpeed === 'slow2g' ? 25 : 
                  connectionSpeed === 'slow' ? 30 :
                  (isMobile ? 35 : 40);
  
  // Use provided quality or fallback to auto quality
  const finalQuality = quality || autoQuality;
  
  // Reduce blur slightly to show more detail in the placeholder
  const blur = isMobile ? 5 : 4;
  
  return getOptimizedImageUrl(src, {
    width: size,
    quality: finalQuality,
    blur
  });
}

/**
 * Preload critical images for faster LCP with network-aware decisions
 */
export function preloadCriticalImages(images: string[]): void {
  if (typeof document === 'undefined') return;
  
  const connectionSpeed = getConnectionSpeed();
  const isSlowConnection = connectionSpeed === 'slow2g' || connectionSpeed === 'slow';
  
  // On very slow connections, only preload the logo
  const criticalImages = isSlowConnection ? 
    images.filter(img => img && img.includes('logo')) : 
    images.filter(img => img && isCriticalPath(img));
  
  // Skip preloading on extremely slow connections
  if (connectionSpeed === 'slow2g' && !isMobile) {
    console.log('Skipping preload on very slow connection');
    return;
  }
  
  // Only preload in the head for critical images
  criticalImages.forEach(src => {
    if (!src) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = getOptimizedImageUrl(src, {
      width: isMobile ? 
        (isSlowConnection ? 320 : 640) : 
        (isSlowConnection ? 640 : 1200),
      quality: isSlowConnection ? 
        (isMobile ? 40 : 50) : 
        (isMobile ? 60 : 75)
    });
    
    // Add to document head if it doesn't already exist
    if (!document.querySelector(`link[rel="preload"][href="${link.href}"]`)) {
      document.head.appendChild(link);
    }
  });
}

/**
 * Calculate image size based on network conditions
 */
export function getNetworkAwareImageSize(defaultSize: number): number {
  const connectionSpeed = getConnectionSpeed();
  const networkStatus = getNetworkStatus();
  
  // On slow connections, reduce image size
  if (connectionSpeed === 'slow2g') {
    return Math.min(defaultSize, 320);
  }
  
  if (connectionSpeed === 'slow') {
    return Math.min(defaultSize, 480);
  }
  
  // For 3G or slow 4G
  if (connectionSpeed === 'medium' || 
     (connectionSpeed === 'fast' && networkStatus.downlink && networkStatus.downlink < 2)) {
    return Math.min(defaultSize, isMobile ? 640 : 800);
  }
  
  // For good connections, use requested size
  return defaultSize;
}
